import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { BUSINESS_UNIT_DETAILS, EST_ACTIVITY_IDS } from '../common/real-estate-form-options';

@Component({
  selector: 'app-land-services-accounting-codeblock',
  templateUrl: './land-services-accounting-codeblock.component.html',
  styleUrls: ['./land-services-accounting-codeblock.component.scss'],
  standalone: false,
})
export class LandServicesAccountingCodeblockComponent implements OnInit, OnChanges {
  @Input() reviewMode: boolean = false;
  @Input() landServicesAccountingForm: FormGroup;

  businessUnitDetails = BUSINESS_UNIT_DETAILS;

  // Capital dropdowns
  capitalBusinessUnits: string[] = [];
  capitalRespCenters: string[] = [];
  capitalOperatingUnits: string[] = [];
  capitalAccounts: string[] = [];
  capitalProcessCodes: string[] = [];

  // O&M dropdowns
  omBusinessUnits: string[] = [];
  omRespCenters: string[] = [];
  omOperatingUnits: string[] = [];
  omAccounts: string[] = [];
  omProcessCodes: string[] = [];

  constructor() {}

  ngOnChanges(changes: SimpleChanges): void {
    this.landServicesAccountingForm.get('capital.projectChargeIndicator').setValue('CWIP');
    this.landServicesAccountingForm.get('om.projectChargeIndicator').setValue('O&M');
  }

  ngOnInit() {
    this.initializeDropdowns();
    this.setupValueChangeListeners();
  }

  initializeDropdowns() {
    // Get unique business units
    const uniqueBusinessUnits = [
      ...new Set(this.businessUnitDetails.map((item) => item.businessUnit)),
    ];
    this.capitalBusinessUnits = uniqueBusinessUnits;
    this.omBusinessUnits = uniqueBusinessUnits;
  }

  setupValueChangeListeners() {
    // Capital form listeners
    this.landServicesAccountingForm.get('capital.businessUnit').valueChanges.subscribe((value) => {
      this.onCapitalBusinessUnitChange(value);
    });

    this.landServicesAccountingForm
      .get('capital.responsibilityCenter')
      .valueChanges.subscribe((value) => {
        this.onCapitalRespCenterChange(value);
      });

    this.landServicesAccountingForm.get('capital.operatingUnit').valueChanges.subscribe((value) => {
      this.onCapitalOperatingUnitChange(value);
    });

    // O&M form listeners
    this.landServicesAccountingForm.get('om.businessUnit').valueChanges.subscribe((value) => {
      this.onOmBusinessUnitChange(value);
    });

    this.landServicesAccountingForm
      .get('om.responsibilityCenter')
      .valueChanges.subscribe((value) => {
        this.onOmRespCenterChange(value);
      });

    this.landServicesAccountingForm.get('om.operatingUnit').valueChanges.subscribe((value) => {
      this.onOmOperatingUnitChange(value);
    });
  }

  // Capital change handlers
  onCapitalBusinessUnitChange(businessUnit: string) {
    if (!businessUnit) {
      this.capitalRespCenters = [];
      this.capitalOperatingUnits = [];
      this.capitalAccounts = [];
      this.capitalProcessCodes = [];
      return;
    }

    const filtered = this.businessUnitDetails.filter((item) => item.businessUnit === businessUnit);
    this.capitalRespCenters = [...new Set(filtered.map((item) => item.respCenter))];

    // Clear dependent fields
    this.landServicesAccountingForm
      .get('capital.responsibilityCenter')
      .setValue('', { emitEvent: false });
    this.landServicesAccountingForm.get('capital.operatingUnit').setValue('', { emitEvent: false });
    this.landServicesAccountingForm.get('capital.account').setValue('', { emitEvent: false });
    this.landServicesAccountingForm.get('capital.processCode').setValue('', { emitEvent: false });
  }

  onCapitalRespCenterChange(respCenter: string) {
    const businessUnit = this.landServicesAccountingForm.get('capital.businessUnit').value;
    if (!businessUnit || !respCenter) {
      this.capitalOperatingUnits = [];
      this.capitalAccounts = [];
      this.capitalProcessCodes = [];
      return;
    }

    const filtered = this.businessUnitDetails.filter(
      (item) => item.businessUnit === businessUnit && item.respCenter === respCenter
    );
    this.capitalOperatingUnits = [...new Set(filtered.map((item) => item.capitalOperUnit))];

    // Clear dependent fields
    this.landServicesAccountingForm.get('capital.operatingUnit').setValue('', { emitEvent: false });
    this.landServicesAccountingForm.get('capital.account').setValue('', { emitEvent: false });
    this.landServicesAccountingForm.get('capital.processCode').setValue('', { emitEvent: false });
  }

  onCapitalOperatingUnitChange(operatingUnit: string) {
    const businessUnit = this.landServicesAccountingForm.get('capital.businessUnit').value;
    const respCenter = this.landServicesAccountingForm.get('capital.responsibilityCenter').value;

    if (!businessUnit || !respCenter || !operatingUnit) {
      this.capitalAccounts = [];
      this.capitalProcessCodes = [];
      return;
    }

    const filtered = this.businessUnitDetails.filter(
      (item) =>
        item.businessUnit === businessUnit &&
        item.respCenter === respCenter &&
        item.capitalOperUnit === operatingUnit
    );

    this.capitalAccounts = [...new Set(filtered.map((item) => item.capitalAccount))];
    this.capitalProcessCodes = [
      ...new Set(filtered.map((item) => item.processCode).filter((code) => code !== '')),
    ];

    // Auto-populate if only one option
    if (this.capitalAccounts.length === 1) {
      this.landServicesAccountingForm
        .get('capital.account')
        .setValue(this.capitalAccounts[0], { emitEvent: false });
    }
    if (this.capitalProcessCodes.length === 1) {
      this.landServicesAccountingForm
        .get('capital.processCode')
        .setValue(this.capitalProcessCodes[0], { emitEvent: false });
    }
  }

  // O&M change handlers
  onOmBusinessUnitChange(businessUnit: string) {
    if (!businessUnit) {
      this.omRespCenters = [];
      this.omOperatingUnits = [];
      this.omAccounts = [];
      this.omProcessCodes = [];
      return;
    }

    const filtered = this.businessUnitDetails.filter((item) => item.businessUnit === businessUnit);
    this.omRespCenters = [...new Set(filtered.map((item) => item.respCenter))];

    // Clear dependent fields
    this.landServicesAccountingForm
      .get('om.responsibilityCenter')
      .setValue('', { emitEvent: false });
    this.landServicesAccountingForm.get('om.operatingUnit').setValue('', { emitEvent: false });
    this.landServicesAccountingForm.get('om.account').setValue('', { emitEvent: false });
    this.landServicesAccountingForm.get('om.processCode').setValue('', { emitEvent: false });
  }

  onOmRespCenterChange(respCenter: string) {
    const businessUnit = this.landServicesAccountingForm.get('om.businessUnit').value;
    if (!businessUnit || !respCenter) {
      this.omOperatingUnits = [];
      this.omAccounts = [];
      this.omProcessCodes = [];
      return;
    }

    const filtered = this.businessUnitDetails.filter(
      (item) => item.businessUnit === businessUnit && item.respCenter === respCenter
    );
    this.omOperatingUnits = [...new Set(filtered.map((item) => item.omOperUnit))];

    // Clear dependent fields
    this.landServicesAccountingForm.get('om.operatingUnit').setValue('', { emitEvent: false });
    this.landServicesAccountingForm.get('om.account').setValue('', { emitEvent: false });
    this.landServicesAccountingForm.get('om.processCode').setValue('', { emitEvent: false });
  }

  onOmOperatingUnitChange(operatingUnit: string) {
    const businessUnit = this.landServicesAccountingForm.get('om.businessUnit').value;
    const respCenter = this.landServicesAccountingForm.get('om.responsibilityCenter').value;

    if (!businessUnit || !respCenter || !operatingUnit) {
      this.omAccounts = [];
      this.omProcessCodes = [];
      return;
    }

    const filtered = this.businessUnitDetails.filter(
      (item) =>
        item.businessUnit === businessUnit &&
        item.respCenter === respCenter &&
        item.omOperUnit === operatingUnit
    );

    this.omAccounts = [...new Set(filtered.map((item) => item.omAccount))];
    this.omProcessCodes = [
      ...new Set(filtered.map((item) => item.processCode).filter((code) => code !== '')),
    ];

    // Auto-populate if only one option
    if (this.omAccounts.length === 1) {
      this.landServicesAccountingForm
        .get('om.account')
        .setValue(this.omAccounts[0], { emitEvent: false });
    }
    if (this.omProcessCodes.length === 1) {
      this.landServicesAccountingForm
        .get('om.processCode')
        .setValue(this.omProcessCodes[0], { emitEvent: false });
    }
  }

  getFilteredActivities(formType: string) {
    const expendType = formType === 'capital' ? 'Capital Exp' : 'Operating Exp';
    return EST_ACTIVITY_IDS.filter((activity) => activity.expendType === expendType);
  }
}
